/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import { StreamingLiveCommerceCareerPaymentV2 } from "components/streaming/liveCommerce/StreamingLiveCommerceCareerPaymentV2";
import { StreamingLiveCommerceConveniencePaymentV2 } from "components/streaming/liveCommerce/StreamingLiveCommerceConveniencePaymentV2";
import { StreamingLiveCommerceCreditPaymentV2 } from "components/streaming/liveCommerce/StreamingLiveCommerceCreditPaymentV2";
import { SETTLEMENT_FEE_DATA } from "constants/purchase";
import { useI18n } from "hooks/i18n/i18n";
import React from "react";
import { useFormContext } from "react-hook-form";
import { useSelector } from "store";
import { AboutPaymentMethods } from "ui/Streaming/LiveCommerce/AboutPaymentMethods";

import type { PaymentFields } from "../StreamingLiveCommerceV2";

export const StreamingLiveCommercePaymentMethodV2: React.FC<{
  liveCommerceRef: React.RefObject<HTMLDivElement>;
}> = ({ liveCommerceRef }) => {
  const { register, watch } = useFormContext<PaymentFields>();
  const selectedPaymentType = watch("paymentType");
  const isDarkModeEnabled = useSelector(
    (state) => state.streaming.streamingSettings.isDarkModeEnabled
  );

  const { t } = useI18n();

  const classesLiveCommercePaymentMethodDesign =
    liveCommercePaymentMethodDesign({
      isDarkModeEnabled,
    });

  /**
   * @description
   * ラジオボタンで支払い方法を切り替えると親要素の高さが変わりスクロール位置がずれるため、高さの変化後に setTimeout でスクロール位置を調整する。
   * クリックされたラジオボタンの親のli要素をスクロール対象とする。
   */
  const handleScrollAdjustment = (targetElement: HTMLInputElement) => {
    const liElement = targetElement.closest("li");

    if (!liElement) return;

    setTimeout(() => {
      const container = liveCommerceRef.current;
      if (container && liElement) {
        const containerRect = container.getBoundingClientRect();
        const elementRect = liElement.getBoundingClientRect();
        const newScrollTop =
          container.scrollTop + elementRect.top - containerRect.top;

        container.scrollTo({
          top: newScrollTop,
          behavior: "smooth",
        });
      }
    }, 0);
  };

  const paymentTypeRegister = register("paymentType");

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        gap: 12px;
      `}
    >
      <p
        css={css`
          color: ${isDarkModeEnabled ? "#fff" : "#000"};
          font-size: 16px;
          font-weight: 700;
        `}
      >
        {t("streaming.commerceCart.payment")}
      </p>
      <ul css={classesLiveCommercePaymentMethodDesign.paymentMethodList}>
        <li>
          <input
            type="radio"
            className="radio-input"
            id={`payment-card`}
            value="Card"
            {...paymentTypeRegister}
            onChange={async (e) => {
              const target = e.currentTarget;
              await paymentTypeRegister.onChange(e);
              handleScrollAdjustment(target);
            }}
          />
          <label htmlFor={`payment-card`}>
            {t("streaming.commerceCart.paymentCard")}
          </label>
          {selectedPaymentType === "Card" && (
            <StreamingLiveCommerceCreditPaymentV2
              isDarkModeEnabled={isDarkModeEnabled}
            />
          )}
        </li>
        <li>
          <input
            type="radio"
            className="radio-input"
            id={`payment-cvs`}
            value="CVS"
            {...paymentTypeRegister}
            onChange={async (e) => {
              const target = e.currentTarget;
              await paymentTypeRegister.onChange(e);
              handleScrollAdjustment(target);
            }}
          />
          <label htmlFor={`payment-cvs`}>
            {t("streaming.commerceCart.paymentCvs")}
            <br />({t("streaming.commerceCart.settlementFee")} ¥
            {SETTLEMENT_FEE_DATA["CVS"].toLocaleString()})
          </label>
          {selectedPaymentType === "CVS" && (
            <StreamingLiveCommerceConveniencePaymentV2
              isDarkModeEnabled={isDarkModeEnabled}
            />
          )}
        </li>
        <li>
          <input
            type="radio"
            className="radio-input"
            id={`payment-phone`}
            value="Phone"
            {...paymentTypeRegister}
            onChange={async (e) => {
              const target = e.currentTarget;
              await paymentTypeRegister.onChange(e);
              handleScrollAdjustment(target);
            }}
          />
          <label htmlFor={`payment-phone`}>
            {t("streaming.commerceCart.paymentPhone")}
            <br />({t("streaming.commerceCart.settlementFee")} ¥
            {SETTLEMENT_FEE_DATA["Phone"].toLocaleString()})
          </label>
          {selectedPaymentType === "Phone" && (
            <StreamingLiveCommerceCareerPaymentV2
              isDarkModeEnabled={isDarkModeEnabled}
            />
          )}
        </li>
      </ul>

      <AboutPaymentMethods isDarkModeEnabled={isDarkModeEnabled} />
    </div>
  );
};

type LiveCommercePaymentMethodDesignProps = {
  isDarkModeEnabled: boolean;
};

const liveCommercePaymentMethodDesign = (
  props: LiveCommercePaymentMethodDesignProps
) => {
  const { isDarkModeEnabled } = props;
  const textColorInDarkMode = isDarkModeEnabled
    ? `
      color: #FFFFFF;
    `
    : `
      color: #000000;
    `;
  const paymentMethodList = css`
    li {
      ${textColorInDarkMode}
      font-size: 14px;
      background-color: ${isDarkModeEnabled ? "#141414" : "#f5f5f5"};
      border: 1px solid;
      border-color: ${isDarkModeEnabled
        ? "rgba(255, 255, 255, 0.2)"
        : "#e0e0e0"};
      &:not(:last-child) {
        margin-bottom: 16px;
      }
    }
    .radio-input {
      display: none;
    }
    .radio-input + label {
      position: relative;
      display: flex;
      align-items: center;
      width: 100%;
      padding: 16px 20px 16px 60px;
      cursor: pointer;
      color: ${props?.isDarkModeEnabled ? "#FFFFFF" : "#000000"};
      border-radius: 4px;
      transition: background-color 0.2s;
    }
    .radio-input + label::before {
      content: "";
      position: absolute;
      top: 50%;
      left: 20px;
      transform: translateY(-50%);
      width: 24px;
      height: 24px;
      background-color: transparent;
      border: 2px solid ${props?.isDarkModeEnabled ? "#FFFFFF" : "#000000"};
      border-radius: 50%;
      box-sizing: border-box;
    }
    .radio-input:checked + label:before {
      background-color: ${props?.isDarkModeEnabled ? "#FFFFFF" : "#000000"};
    }
    .radio-input:checked + label::after {
      content: "";
      position: absolute;
      top: 50%;
      left: 29px;
      transform: translateY(-50%);
      width: 6px;
      height: 6px;
      background-color: ${props?.isDarkModeEnabled ? "#000000" : "#FFFFFF"};
      border-radius: 50%;
    }
  `;

  return {
    paymentMethodList,
  };
};
